'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import CartLineItem from '@/components/cart/CartLineItem';
import { useCart } from '@/context/CartContext';
import { useI18n } from '@/i18n/LocaleProvider';
import { formatPrice } from '@/i18n/config';

export default function CartDrawer() {
  const { lines, subtotal, hasPreorder, drawerOpen, closeDrawer } = useCart();
  const { locale, t } = useI18n();

  useEffect(() => {
    if (!drawerOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen, closeDrawer]);

  return (
    <div
      className={`fixed inset-0 z-[70] transition-opacity duration-300 ${drawerOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
      aria-hidden={!drawerOpen}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeDrawer} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t.cart.title}
        className={`absolute right-0 top-0 h-full w-full max-w-md bg-background border-l border-border flex flex-col transition-transform duration-300 ${drawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-bold text-foreground">{t.cart.title}</h2>
          <button
            onClick={closeDrawer}
            className="w-9 h-9 flex items-center justify-center rounded-full glass-card border border-border text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t.cart.close}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {lines.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <p className="text-xl font-bold text-foreground mb-2">{t.cart.empty}</p>
            <p className="text-sm text-muted-foreground mb-6">{t.cart.emptyHint}</p>
            <Link
              href="/category"
              onClick={closeDrawer}
              className="px-6 py-3 rounded-full text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {t.cart.continueShopping}
            </Link>
          </div>
        ) : (
          <>
            <ul className="flex-1 overflow-y-auto px-6 divide-y divide-border">
              {lines.map((line) => (
                <CartLineItem key={line.product.id} line={line} onNavigate={closeDrawer} />
              ))}
            </ul>
            <div className="border-t border-border px-6 py-5 flex-shrink-0">
              {hasPreorder && (
                <p className="mb-4 text-xs text-muted-foreground rounded-xl bg-accent/10 border border-accent/30 px-3 py-2">{t.cart.preorderNote}</p>
              )}
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-foreground">{t.cart.subtotal}</span>
                <span className="text-lg font-extrabold text-foreground tabular-nums">{formatPrice(subtotal, locale)}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-4">{t.cart.shippingAtCheckout}</p>
              <Link
                href="/checkout"
                onClick={closeDrawer}
                className="block w-full text-center py-3.5 rounded-full text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                {t.cart.checkout}
              </Link>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
